// import '../utils/wdyr';
import { useRouter } from 'next/router';
import 'lazysizes';
import { Provider, useStore } from 'react-redux';
import { ThemeProvider } from 'emotion-theming';
import * as Sentry from '@sentry/node';
import { ExtraErrorData } from '@sentry/integrations';
import dynamic from 'next/dynamic';
import NProgress from 'accessible-nprogress';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import sha1 from 'sha1';
import App from 'next/app';
import nextCookies from 'next-cookies';

/* Components */
import ToastContainer from '../atoms/toast-container';

/* Utils */
import contentGrouping from '../utils/contentGrouping';
import deleteCookies from '../utils/deleteCookies';
import getCookie from '../utils/getCookie';
import hasError from '../utils/hasError';
import Amplitude, { logAmplitude, setUser as setUserAmplitude } from '../utils/amplitude';
import { trackPageType } from '../utils/tracking';
import { SiteSpectProvider } from '../utils/context/sitespect-provider';
import { SearchProvider } from '../utils/context/search-provider';
import { getPromoContent, getMobileNavigation } from '../cms-generals';
import { sortNavigation } from '../navigation';
import '../utils/algolia';

import {
  addTabToStorage,
  removeTabFromStorage,
  setTabTitle
} from '../utils/flashingTabHelpers';

/* Redux Store */
import { setMobileNavigation, setDesktopNavigation, setDesktopNavigationVariation } from '../store/modules/navigation/actions';
import { setPetiteProfiles, updateActiveMini } from '../store/modules/petites/actions';
import { setPromotions } from '../store/modules/promotions/actions';
import { storeWrapper } from '../store';
import { updateWishlist } from '../store/modules/lists/actions';
import {
  toggleUserLoading,
  setUserProfile,
  resetUserProfile,
  setAlgoliaGuestUserToken
} from '../store/modules/profile/actions';

import {
  resetUser,
  setLastPageVisited,
  setToken,
  updateIsSubscribedToReceiveEmails
} from '../store/modules/user/actions';

import {
  toggleCartModalVisibility,
  toggleNavigationVisibility,
  togglePetiteDropdownState,
  setDocumentTitle
} from '../store/modules/interfaces/actions';

import {
  updateCart,
  updateCount,
  updateCartLoading,
  removeCart
} from '../store/modules/cart/actions';

/* Theme */
import theme from '../theme';
import GlobalStyles from '../theme/styles';

/* API */
import {
  getCart,
  getCurrentCart,
  getDefaultWishlist,
  getGenerals,
  getMinis,
  getNavigation,
  getNavigationTest,
  getUser,
  identifyKustomer
} from './api';

/* Layout */
import DefaultLayout from '../layouts/default';

const Footer = dynamic(() => import('../organs/footer'));
const Subscribe = dynamic(() => import('../organs/subscribe'));

/* Constants */
const VALID_USER_TOKEN_LENGTH = 48;
const SUBSCRIBE_WAIT_DURATION = 10000;

let PENDING_LUX_TRACKING = false;
let SUBSCRIBE_SHOW_TIMER = null;

Sentry.init({
  environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  release: process.env.npm_package_version,
  integrations: [new ExtraErrorData()]
});

Amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_KEY, null,
  { includeUtm: true, includeReferrer: true, saveParamsReferrerOncePerSession: false });

const Maisonette = (props) => {
  // eslint-disable-next-line react/destructuring-assignment
  const { Component, pageProps } = props;
  const Layout = Component.Layout || DefaultLayout;

  // ?? {} used to load router on server
  const router = useRouter() ?? {};
  const store = useStore();

  const history = useRef([]);
  const [showModal, setShowModal] = useState(0);
  const [subscribedToEmails, setIsSubscribedToEmails] = useState(0);
  const { flashing_tab = false } = props.siteSpectTests;

  const SUBSCRIBE_BLACKLIST = [
    '/account',
    '/orders',
    '/returns',
    '/credits',
    '/petite-profiles',
    '/login',
    '/password',
    '/signup',
    '/checkout',
    '/checkout-new'
  ];

  const containsBlacklistedString = (urlPath) =>
    SUBSCRIBE_BLACKLIST.some((str) => urlPath.includes(str));

  const toggleEmailModal = (url) => {
    const disableSubscription = router.query.disable_subscribe === '1';
    const emailSubscription = getCookie('subscribed_to_emails');
    const emailSubscriptionSuppress = getCookie('subscribed_to_emails_suppression');

    store.dispatch(
      updateIsSubscribedToReceiveEmails(
        emailSubscriptionSuppress || emailSubscription || disableSubscription
      )
    );

    if (!containsBlacklistedString(url) && !(subscribedToEmails || emailSubscriptionSuppress)) {
      SUBSCRIBE_SHOW_TIMER = setTimeout(() => setShowModal(true), SUBSCRIBE_WAIT_DURATION);
    }
  };

  const loadKustomer = (id = null) => {
    // let this catch up before executing
    setTimeout(() => {
      if (process.env.NEXT_PUBLIC_KUSTOMER_ACTIVE === 'true') {
        try {
          identifyKustomer({ id });
        } catch (e) {
          identifyKustomer({ id: null });
        }
      }
    }, 0);
  };

  const fetchCurrentCart = () => {
    store.dispatch(updateCartLoading(true));

    getCurrentCart().then((cart) => {
      if (hasError(cart)) {
        cart.errors.map((error) => Sentry.captureMessage(error));
        store.dispatch(updateCartLoading(false));
        return;
      }

      // save cart to store
      if (cart) store.dispatch(updateCart(cart));
      else store.dispatch(updateCount(0));

      store.dispatch(updateCartLoading(false));
    }).catch(() => deleteCookies(true));
  };

  const fetchUserData = () => {
    const userData = getCookie('maisonette_user_data');

    const saveUserDataToStore = (user) => {
      const spreeApiKey = getCookie('maisonette_user_token');

      if (spreeApiKey && user) {
        // save spree_api_key
        store.dispatch(setToken('spree_api_key', spreeApiKey));
        // save user to store
        store.dispatch(setUserProfile(user));
        // set user subscribed cookie
        setIsSubscribedToEmails(user.subscribed);
        // check to show email modal
        toggleEmailModal('/');
        // load kustomer
        loadKustomer(user.id);
      }
    };

    if (userData) {
      const user = JSON.parse(userData);
      saveUserDataToStore(user);
    }

    getUser().then((user) => {
      // reset Sentry user scope
      Sentry.configureScope((scope) => scope.setUser(null));
      if (hasError(user)) {
        user.errors.map((error) => Sentry.captureMessage(error));
        return;
      }

      // set Sentry user scope if user returned
      Sentry.configureScope((scope) => {
        scope.setUser({ id: user.id, email: user.email });
      });

      // set Amplitude user
      setUserAmplitude(user);

      saveUserDataToStore(user);
    })
      .catch(() => deleteCookies(true))
      .finally(() => store.dispatch(toggleUserLoading(false)));
  };

  const fetchMinis = () => {
    getMinis().then((minis) => {
      if (hasError(minis)) {
        minis.errors.map((error) => Sentry.captureMessage(error));
        return;
      }

      // save petite profile to store
      store.dispatch(setPetiteProfiles(minis));
    }).catch(() => deleteCookies(true));
  };

  const fetchWishlist = () => {
    getDefaultWishlist().then((wishlist) => {
      if (hasError(wishlist)) {
        wishlist.errors.map((error) => Sentry.captureMessage(error));
        return;
      }

      // save wishlist to store
      store.dispatch(updateWishlist(wishlist?.wished_products ?? []));
    }).catch(() => deleteCookies(true));
  };

  const getUserAppData = async () => {
    fetchCurrentCart();
    fetchUserData();
    fetchMinis();
    fetchWishlist();
  };

  const getGuestAppData = () => {
    const token = getCookie('maisonette_order_token');
    const number = getCookie('maisonette_order_number');

    if (token && number) {
      getCart({ order_number: number }).then((cart) => {
        // save cart to store
        if (cart) store.dispatch(updateCart(cart));
        else store.dispatch(updateCount(0));
      }).catch((error) => {
        Sentry.captureException(error);
        // error fetching user data, delete all user data
        deleteCookies(true);
      }).finally(() => {
        store.dispatch(updateCartLoading(false));
      });
    } else {
      store.dispatch(updateCartLoading(false));
    }

    // set Amplitude user
    setUserAmplitude();

    // generate Algolia guest user token and save to store
    let algoliaUserToken = Amplitude?._unsentIdentifys[0]?.event?.uuid;

    if (!algoliaUserToken) {
      const generateGuestUserToken = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let hash = '';

        for (let i = 0; i < 36; i++) {
          if (i === 8 || i === 12 || i === 16 || i === 20) {
            hash += '-';
            // eslint-disable-next-line no-continue
            continue;
          }

          hash += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        return hash;
      };

      algoliaUserToken = generateGuestUserToken();

      Sentry.withScope((scope) => {
        scope.setLevel(Sentry.Severity.Warning);
        Sentry.setExtra('Amplitude Client used to generate invalid guest user token and the generated fallback Algolia user token', {
          Amplitude,
          generatedFallbackAlgoliaUserToken: algoliaUserToken
        });
        Sentry.captureException(new Error('Error generating Algolia guest user token'));
      });
    }

    store.dispatch(setAlgoliaGuestUserToken(algoliaUserToken));

    store.dispatch(toggleUserLoading(false));
  };

  const fetchMainNavigation = () => {
    getNavigation().then((res) => {
      if (hasError(res)) {
        res.errors.map((error) => Sentry.captureMessage(error));
        return;
      }

      store.dispatch(setDesktopNavigation(res));
    }).catch((error) => Sentry.captureMessage(error));
  };

  const fetchNavigationVariation = () => {
    getNavigationTest({ headers: { 'X-Variation': 'Navigation V1' } }).then((res) => {
      if (hasError(res)) {
        res.errors.map((error) => Sentry.captureMessage(error));
        return;
      }

      store.dispatch(setDesktopNavigationVariation(sortNavigation(res)));
    }).catch((error) => Sentry.captureMessage(error));
  };

  const storeMobileNavigation = (siteConstants) => {
    try {
      store.dispatch(setMobileNavigation(getMobileNavigation(siteConstants)));
    } catch (error) {
      Sentry.captureMessage(error);
    }
  };

  const storePromoContent = (siteConstants) => {
    try {
      store.dispatch(setPromotions(getPromoContent(siteConstants)));
    } catch (error) {
      Sentry.captureMessage(error);
    }
  };

  const getAppConstants = async () => {
    const siteConstants = await getGenerals();

    fetchMainNavigation();
    fetchNavigationVariation();
    storeMobileNavigation(siteConstants);
    storePromoContent(siteConstants);
  };

  const getAppData = () => {
    getAppConstants();

    const token = getCookie('maisonette_user_token');

    if (token && token.length !== VALID_USER_TOKEN_LENGTH) {
      global.document.cookie = 'maisonette_user_token=; Max-Age=0; path=/;';
      global.document.cookie = 'maisonette_user_data=; Max-Age=0; path=/;';
      return;
    }

    try {
      if (!getCookie('maisonette_session_token')) {
        global.fetch('/api/session-token')
          .then((r) => r.json())
          .then((response) => {
            global.document.cookie = `maisonette_session_token=${response.token}; path=/;`;
          });
      }
    } catch (error) {
      Sentry.captureMessage(error);
    }

    if (token) getUserAppData();
    else getGuestAppData();
  };

  const setPageType = (url) => {
    const path = url || router.asPath || global.pathname;

    const pageType = path === '/404'
      ? 'Page Not Found'
      : contentGrouping(props);

    trackPageType(pageType, path);

    Sentry.setTags({
      'page.group': pageType,
      'page.path': global.window.location.pathname
    });
  };

  const checkIsValidUser = () => {
    const { user } = store.getState();
    const token = getCookie('maisonette_user_token');

    if (user.spree_api_key !== token) {
      store.dispatch(resetUser());
      store.dispatch(resetUserProfile());
      store.dispatch(removeCart());
      deleteCookies();
    }
  };

  const resetInterfaces = (url) => {
    const activeMini = getCookie('maisonette_active_mini');
    store.dispatch(setLastPageVisited(url));
    store.dispatch(updateActiveMini(+activeMini));
    store.dispatch(toggleNavigationVisibility(false));
    store.dispatch(togglePetiteDropdownState(false));
    store.dispatch(toggleCartModalVisibility(false));
  };

  const identifyUser = () => {
    const userCookie = getCookie('maisonette_user_data');
    const user = userCookie ? JSON.parse(userCookie) : null;

    global.ire('identify', {
      customerId: user?.id ?? '',
      customerEmail: user?.email
        ? sha1(user.email)
        : ''
    });
  };

  const onRouteChangeStart = (url, { shallow }) => {
    if (PENDING_LUX_TRACKING) {
      global.LUX.send();
      PENDING_LUX_TRACKING = false;
    }

    global.LUX.init();

    if (!shallow) {
      NProgress.start();
    }

    checkIsValidUser();

    // close all modals and set previous page active
    resetInterfaces(url);

    if (SUBSCRIBE_SHOW_TIMER) {
      clearTimeout(SUBSCRIBE_SHOW_TIMER);
      SUBSCRIBE_SHOW_TIMER = null;
    }
  };

  const saveDocumentTitle = () => {
    // save document title to redux to be used to reset title
    // after returning to Maisonette tab or if number of times reached
    store.dispatch(setDocumentTitle(global.document.title));
  };

  const storeDataForFlashingTab = () => {
    if (flashing_tab) {
      // store document title in redux
      saveDocumentTitle();
      // save the tab number to local storage
      addTabToStorage();
    }
  };

  const handleFlashingTab = () => {
    const { visibilityState } = global.document;
    let cartCount = getCookie('maisonette_cart_count');
    const { documentTitle } = store.getState().interfaces;
    const getOpenTabs = () => JSON.parse(global.localStorage.getItem('maisonetteTabs') ?? JSON.stringify([]));
    let openTabs = getOpenTabs();
    // need to check for checkout confirmation page
    // // the cart still exists until the user leaves that page, so tab would still flash
    const isCheckoutConfirm = global.window.location.pathname.includes('/checkout/confirmation/');

    if (visibilityState === 'hidden' && cartCount > 0 && !isCheckoutConfirm) {
      const getTitles = (count) => [`(${count}) 👋 Don't forget`, 'Your picks are ready!'];

      let index = 0;
      let counter = 0;
      let titles = getTitles(cartCount);
      setTabTitle(titles[index]);

      global.titleChange = setInterval(() => {
        if (index === 0) index = 1;
        else index = 0;
        counter += 1;
        // update cartCount and openTabs data to update inactive tab titles
        cartCount = getCookie('maisonette_cart_count');
        openTabs = getOpenTabs();
        titles = getTitles(cartCount);
        if ((counter >= 6 && openTabs.length > 2) || cartCount < 1) {
          setTabTitle(documentTitle);
          clearInterval(global.titleChange);
          return;
        }
        setTabTitle(titles[index]);
      }, 2000);
    } else {
      setTabTitle(documentTitle);
      clearInterval(global.titleChange);
    }

    return () => {
      clearInterval(global.titleChange);
    };
  };

  const onRouteChangeComplete = (url) => {
    global.LUX.send();
    setPageType(url);
    NProgress.done();
    toggleEmailModal(url);
    storeDataForFlashingTab();

    if (history?.current?.length) {
      const previous = history?.current[history?.current?.length - 1];
      if (previous && previous !== url) identifyUser();
    }
  };

  const onRouteChangeError = () => {
    NProgress.done();
  };

  const setActiveMini = () => {
    const mini = getCookie('maisonette_active_mini');
    if (mini) store.dispatch(updateActiveMini(+mini));
  };

  const resetAppState = () => {
    store.dispatch(toggleNavigationVisibility(false));
    store.dispatch(togglePetiteDropdownState(false));
    store.dispatch(toggleCartModalVisibility(false));
  };

  // component did mount
  useEffect(() => {
    getAppData();
    setPageType();
    setActiveMini();
    resetAppState();
    storeDataForFlashingTab();

    const { utmMedium } = router.query;

    if (utmMedium === 'email') {
      global.document.cookie = 'subscribed_to_emails=1; path=/';
      global.document.cookie = 'subscribed_to_emails_suppression=1; Max-Age=31556926';
    }

    toggleEmailModal(router.asPath);

    global.history.scrollRestoration = 'manual';

    NProgress.configure({ trickleSpeed: 400 });

    identifyUser();

    // bind events
    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteChangeComplete);
    router.events.on('routeChangeError', onRouteChangeError);
    if (flashing_tab) {
      global.document.addEventListener('visibilitychange', handleFlashingTab);
      global.window.addEventListener('unload', removeTabFromStorage);
    }

    // unbind events
    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      router.events.off('routeChangeComplete', onRouteChangeComplete);
      router.events.off('routeChangeError', onRouteChangeError);
      if (flashing_tab) {
        global.document.removeEventListener('visibilitychange', handleFlashingTab);
        global.window.removeEventListener('unload', removeTabFromStorage);
      }
    };
  }, []);

  // component did update
  useEffect(() => {
    if (history.current[history.length - 1] !== router.asPath) {
      history.current = [...history.current, router.asPath];
    }

    Sentry.configureScope((scope) => {
      try {
        // send cookies to sentry
        const allCookies = getCookie();
        scope.setContext('cookies', allCookies);
      } catch (e) { /* noop */ }

      try {
        // send local storage to sentry
        scope.setContext('local storage', { ...global.localStorage });
      } catch (e) { /* noop */ }

      try {
        // send cart state to sentry
        const state = Object.entries(
          store.getState().cart
        ).reduce((acc, [key, value]) => {
          const temp = acc;

          // stringify store values since Sentry
          // doesn't expand objects in context
          if (typeof temp[key] !== 'object') {
            temp[key] = JSON.stringify(value);
          }

          return temp;
        }, {});
        scope.setContext('cart', state);
      } catch (e) { /* noop */ }
    });
  });

  useEffect(() => {
    // tracks Amplitude page view events
    // some page view events require data
    const amplitudeProps = router.asPath === '/404' ? { error: 404 } : props.pageProps;
    logAmplitude(null, amplitudeProps);
  }, [props.pageProps]);

  return (
    <Provider store={store}>
      {GlobalStyles}

      <a className="skiptocontent" href="#maincontent">skip to main content</a>

      <ToastContainer />

      <SiteSpectProvider value={props.siteSpectTests}>
        <SearchProvider>
          <ThemeProvider theme={theme}>
            <Layout>
              {
                process.env.NEXT_PUBLIC_SHOW_EMAIL_MODAL === 'true'
                && !store.getState().user?.isSubscribedToReceiveEmails
                && showModal
                && !containsBlacklistedString(router.asPath) ? <Subscribe /> : null
              }

              <Component
                {...pageProps}
                store={store}
                key={router.route}
                history={history?.current ?? []}
              />

              <Footer />
            </Layout>
          </ThemeProvider>
        </SearchProvider>
      </SiteSpectProvider>
    </Provider>
  );
};

Maisonette.defaultProps = {
  Component: null,
  pageProps: {},
  siteSpectTests: {}
};

Maisonette.propTypes = {
  Component: PropTypes.elementType,
  pageProps: PropTypes.object,
  siteSpectTests: PropTypes.object
};

Maisonette.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext);
  const cookies = nextCookies(appContext.ctx);

  const siteSpectTests = Object.entries(cookies)
    .reduce((acc, [key, value]) => {
      const temp = acc;
      if (key.startsWith('sitespect_')) {
        const name = key.replace('sitespect_', '');
        temp[name] = value;
      }
      return temp;
    }, {});

  return { siteSpectTests, ...appProps };
};

export default storeWrapper.withRedux(Maisonette);
